<script lang="ts" setup>
import type { FAQ } from "@/types";
const { blok } = defineProps<{ blok: FAQ }>();

const items = computed(() =>
  blok.questions.map((question) => ({
    label: question.question,
    ...question,
  })),
);
</script>

<template>
  <Container
    v-editable="blok"
    class="py-2.5 lg:py-20"
    :style="{ backgroundColor: blok?.backgroundColor ?? '#8C9486' }"
  >
    <div class="grid grid-cols-1 gap-4 lg:gap-20 lg:px-0 lg:grid-cols-2">
      <div
        class="flex items-center px-2.5 pb-5 pt-[30px] lg:p-0 justify-start"
        :class="{ 'lg:order-last': blok.arrangement === 'right' }"
      >
        <div class="flex flex-col justify-center items-start gap-[5px]">
          <p class="font-helvetica-bold">{{ blok.subtitle }}</p>
          <h2 class="font-helvetica-light text-[25px] lg:text-[33px]">
            {{ blok.heading }}
          </h2>
        </div>
      </div>
      <div>
        <UAccordion
          variant="solid"
          :items="items"
          :ui="{ item: { base: 'py-7' } }"
          class="rounded-[6px] overflow-hidden px-5 bg-white"
        >
          <template #default="{ item, open, index }">
            <div
              class="flex justify-between items-start py-5 md:py-[30px] md:px-5 bg-white text-lg lg:text-xl font-helvetica-bold cursor-pointer"
              :class="{
                'border-b border-b-[#999]': !open && items.length - 1 !== index,
              }"
            >
              <span>{{ item.label }}</span>
              <UIcon
                name="i-heroicons-chevron-down-20-solid"
                class="w-5 h-5 ms-auto transform transition-transform duration-200 shrink-0"
                :class="[open && 'rotate-180']"
              />
            </div>
          </template>

          <template #item="{ item, open, index }">
            <div
              class="text-[#1D1F22] md:px-5 pt-0 pb-5"
              :class="{
                'border-b border-b-[#999]': open && items.length - 1 !== index,
              }"
            >
              <RichText v-editable="item" :content="item.answer" />
              <div class="grid grid-cols-2 gap-2.5">
                <BlokLink
                  v-for="button in item.buttons"
                  :key="button._uid"
                  :blok="button.url"
                  :is-form="button.isForm"
                  class="flex gap-6 py-5"
                >
                  <span class="font-helvetica-bold">{{ button.text }}</span>
                  <NuxtImg
                    width="24px"
                    height="24px"
                    src="/icon/chevron-right-rounded.svg"
                    alt="Chevron right"
                  />
                </BlokLink>
              </div>
            </div>
          </template>
        </UAccordion>
      </div>
    </div>
  </Container>
</template>
